// Layer 4 (UI) — the condition panel of the Maintenance panel row (polish.P21,
// comp A: the left eight columns). Presentational: it renders one
// `ConditionDetailRead` — the selected monitoring point, BM01 trunnion bearing
// temperature by default — and holds no state of its own. The selection lives
// in the scene, so the Condition monitoring table below the fold chooses what
// this panel draws on every tab.
//
// The panel is the point drawn against its own historian series: its heading
// (the point, its tag, the constraint chip where the item sits on the plant's
// constraint), the item's register line, the trend on a time axis with its
// forward half, and four figures under it. Nothing is typed here; every figure
// is a field of the read.
//
// The point's monitoring type and frequency ride the register line's `title`:
// the table on the Condition monitoring tab already carries both columns.
//
// No reading-entry path: OptiFlow reads the historian, it does not write to it
// (ADR-0002).

import { useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassCard, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { ConditionDetailRead, ConditionFigure } from './ConditionDetail.transform';
import { ConditionTrendChart } from './ConditionTrendChart';

/** The trend's own floor; below 1400 px the card stretches to the taller
 *  Pending from Advisory beside it and the chart takes the spare height. At the
 *  comp's 1470 the chart keeps this exact height — the comp draws it so. */
const CHART_HEIGHT = 300;

/** A figure value breaks at its `·`, never inside its last segment: the LIMIT
 *  figure's `14 Jun` stays whole in a narrow column (`≈ 5.8 d ·` / `14 Jun`),
 *  and the span is the figure's own date for the one-line probe. */
function FigureValue({ value }: { value: string }) {
  const at = value.lastIndexOf(' · ');
  if (at < 0) return <>{value}</>;
  return (
    <>
      {value.slice(0, at + 3)}
      <span className="whitespace-nowrap" data-figure-date>
        {value.slice(at + 3)}
      </span>
    </>
  );
}

function Figure({ figure, first }: { figure: ConditionFigure; first: boolean }) {
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-px pr-3 pt-2', first ? '' : 'border-l border-[var(--color-divider)] pl-3.5')}
      data-condition-figure={figure.id}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{figure.label}</span>
      <span
        className={cn(
          'text-[17px] font-semibold leading-tight tabular-nums',
          figure.tone === 'bad' ? 'text-[var(--state-blocked)]' : 'text-ink-primary',
        )}
      >
        <FigureValue value={figure.value} />
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted">{figure.sub}</span>
    </div>
  );
}

export function ConditionDetail({
  read,
  projectId,
}: {
  read: ConditionDetailRead;
  projectId: string;
}) {
  const { panel } = read;
  const caseHref = read.caseId ? `/projects/${projectId}/advisory?case=${read.caseId}` : null;
  // The chart box below 1400 px: the card stretches to the taller Pending from
  // Advisory beside it, so the box takes that spare height and the chart is
  // redrawn at it. The chart is out of flow inside the box, so the box's own
  // height never feeds back into the row it is measuring. At 1470 the box takes
  // no grow and the chart keeps `CHART_HEIGHT`.
  const [chartBox, setChartBox] = useState<HTMLDivElement | null>(null);
  const [chartHeight, setChartHeight] = useState(CHART_HEIGHT);
  useLayoutEffect(() => {
    if (!chartBox) return undefined;
    const measure = () => setChartHeight(Math.max(CHART_HEIGHT, Math.floor(chartBox.getBoundingClientRect().height)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(chartBox);
    return () => ro.disconnect();
  }, [chartBox]);
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label={panel.heading}
      className="flex h-full min-w-0 flex-col px-4 py-3"
      data-condition-panel={read.pointId}
    >
      <header className="mb-1 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink-primary">
          {caseHref ? (
            <Link to={caseHref} className="hover:underline" title={read.caseTitle ?? undefined} data-condition-case-link={read.caseId}>
              {panel.heading}
            </Link>
          ) : (
            panel.heading
          )}
        </h3>
        {panel.constraintChip ? <Pill tone="default">{panel.constraintChip}</Pill> : null}
        <span className="flex-1" />
        <ProvenanceChip text="[syn]" title={read.provenance} compact />
        {read.chip && read.chipLabel ? <Pill tone={read.chipTone}>{read.chipLabel}</Pill> : null}
      </header>

      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-ink-secondary">
        {read.item ? (
          <span className="min-w-0 truncate" title={`${read.monitoringType} · ${read.frequency}`}>
            {read.item.label} · {read.item.criticality} · {read.item.taskType}
          </span>
        ) : (
          <span title={`${read.monitoringType} · ${read.frequency}`}>{read.monitoringType}</span>
        )}
        <span className="flex-1" />
        <span className="text-xs tabular-nums text-muted">{panel.window}</span>
      </div>

      {panel.plot ? (
        <div
          ref={setChartBox}
          className="relative max-[1399px]:grow"
          style={{ minHeight: CHART_HEIGHT }}
          data-condition-chart-box
        >
          <div className="absolute inset-0">
            <ConditionTrendChart
              plot={panel.plot}
              references={read.chart?.references ?? []}
              height={chartHeight}
              label={`${panel.heading} against its warn and limit references`}
              alert={read.chip === 'warn'}
            />
          </div>
        </div>
      ) : null}

      {panel.figures.length > 0 ? (
        <div
          className="mt-1.5 grid border-t border-[var(--color-divider)]"
          style={{ gridTemplateColumns: `repeat(${panel.figures.length}, minmax(0, 1fr))` }}
          data-condition-figures
        >
          {panel.figures.map((figure, index) => (
            <Figure key={figure.id} figure={figure} first={index === 0} />
          ))}
        </div>
      ) : null}
    </GlassCard>
  );
}
